import React, { useState } from 'react';
import { useTimeTracking } from '../context/TimeTrackingContext';
import { useProjects } from '../context/ProjectContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ResourceName } from '../types/timeTracking';
import { PlusIcon, ClockIcon, TrashIcon, CalendarIcon, FolderIcon, UserIcon, FilterIcon, XIcon } from 'lucide-react';
const RESOURCES: ResourceName[] = ['Sergey Kleymuk', 'Sasha Oleksandr', 'Dmitry Syrov', 'David Nssien'];
export function CostTracking() {
  const {
    selectedCompany
  } = useData();
  const {
    getTimeEntriesByCompanyId,
    addTimeEntry,
    deleteTimeEntry
  } = useTimeTracking();
  const {
    getProjectsByCompanyId
  } = useProjects();
  const [newEntry, setNewEntry] = useState({
    project_id: '',
    resource_name: '' as ResourceName | '',
    hours: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [filters, setFilters] = useState({
    project_id: '',
    resource_name: '',
    start_date: '',
    end_date: ''
  });
  const timeEntries = selectedCompany ? getTimeEntriesByCompanyId(selectedCompany.id) : [];
  const projects = selectedCompany ? getProjectsByCompanyId(selectedCompany.id) : [];
  // Apply filters
  const filteredEntries = timeEntries.filter(entry => {
    if (filters.project_id && entry.project_id !== filters.project_id) return false;
    if (filters.resource_name && entry.resource_name !== filters.resource_name) return false;
    const entryDate = new Date(entry.date);
    if (filters.start_date) {
      const startDate = new Date(filters.start_date);
      if (entryDate < startDate) return false;
    }
    if (filters.end_date) {
      const endDate = new Date(filters.end_date);
      if (entryDate > endDate) return false;
    }
    return true;
  });
  const hasActiveFilters = filters.project_id || filters.resource_name || filters.start_date || filters.end_date;
  const clearFilters = () => {
    setFilters({
      project_id: '',
      resource_name: '',
      start_date: '',
      end_date: ''
    });
  };
  const handleAddEntry = () => {
    if (!newEntry.project_id || !newEntry.resource_name || !newEntry.hours || !selectedCompany) {
      alert('Please fill in all required fields (Project, Resource, Hours)');
      return;
    }
    const hours = parseFloat(newEntry.hours);
    if (isNaN(hours) || hours <= 0) {
      alert('Please enter a valid number of hours');
      return;
    }
    // Round to nearest 0.25
    const roundedHours = Math.round(hours * 4) / 4;
    addTimeEntry({
      company_id: selectedCompany.id,
      project_id: newEntry.project_id,
      resource_name: newEntry.resource_name as ResourceName,
      hours: roundedHours,
      notes: newEntry.notes,
      date: new Date(newEntry.date)
    });
    // Reset form
    setNewEntry({
      project_id: '',
      resource_name: '' as ResourceName | '',
      hours: '',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
  };
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.title || 'Unknown Project';
  };
  const getTotalHours = (entries = timeEntries) => {
    return entries.reduce((sum, entry) => sum + entry.hours, 0);
  };
  const getTotalHoursByResource = (entries = timeEntries) => {
    const totals: Record<string, number> = {};
    entries.forEach(entry => {
      totals[entry.resource_name] = (totals[entry.resource_name] || 0) + entry.hours;
    });
    return totals;
  };
  const getTotalHoursByProject = (entries = timeEntries) => {
    const totals: Record<string, number> = {};
    entries.forEach(entry => {
      totals[entry.project_id] = (totals[entry.project_id] || 0) + entry.hours;
    });
    return totals;
  };
  const resourceTotals = getTotalHoursByResource(filteredEntries);
  const projectTotals = getTotalHoursByProject(filteredEntries);
  return <div className="max-w-6xl mx-auto space-y-6" data-id="element-777">
      <div className="flex items-center justify-between" data-id="element-778">
        <div data-id="element-779">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-780">Cost Flow</h1>
          <p className="text-sm text-gray-600" data-id="element-781">
            {hasActiveFilters ? <>
                {filteredEntries.length} of {timeEntries.length} entries •{' '}
                {getTotalHours(filteredEntries).toFixed(2)} hours
              </> : <>
                {timeEntries.length} time entries • {getTotalHours().toFixed(2)}{' '}
                total hours
              </>}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-782">
        <div className="flex items-center justify-between mb-4" data-id="element-783">
          <div className="flex items-center gap-2" data-id="element-784">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-785" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-786">Filters</h2>
          </div>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters} leftIcon={<XIcon className="w-4 h-4" data-id="element-788" />} data-id="element-787">
              Clear Filters
            </Button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-789">
          <div data-id="element-790">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-791">
              Project
            </label>
            <select value={filters.project_id} onChange={e => setFilters({
            ...filters,
            project_id: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-792">
              <option value="" data-id="element-793">All Projects</option>
              {projects.map(project => <option key={project.id} value={project.id} data-id="element-794">
                  {project.title}
                </option>)}
            </select>
          </div>

          <div data-id="element-795">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-796">
              Resource
            </label>
            <select value={filters.resource_name} onChange={e => setFilters({
            ...filters,
            resource_name: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-797">
              <option value="" data-id="element-798">All Resources</option>
              {RESOURCES.map(resource => <option key={resource} value={resource} data-id="element-799">
                  {resource}
                </option>)}
            </select>
          </div>

          <div data-id="element-800">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-801">
              Start Date
            </label>
            <input type="date" value={filters.start_date} onChange={e => setFilters({
            ...filters,
            start_date: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-802" />
          </div>

          <div data-id="element-803">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-804">
              End Date
            </label>
            <input type="date" value={filters.end_date} onChange={e => setFilters({
            ...filters,
            end_date: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-805" />
          </div>
        </div>
      </Card>

      {/* Filtered Summary */}
      {hasActiveFilters && <Card data-id="element-806">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-807">
            Filtered Summary
          </h2>
          <div className="space-y-4" data-id="element-808">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg" data-id="element-809">
              <div className="flex items-center gap-3" data-id="element-810">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-811">
                  <ClockIcon className="w-5 h-5 text-blue-600" data-id="element-812" />
                </div>
                <div data-id="element-813">
                  <p className="text-sm text-gray-600" data-id="element-814">Total Hours</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-815">
                    {getTotalHours(filteredEntries).toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-816">
                <p className="text-sm text-gray-600" data-id="element-817">Entries</p>
                <p className="text-lg font-semibold text-gray-900" data-id="element-818">
                  {filteredEntries.length}
                </p>
              </div>
            </div>

            {/* Filtered Entries Details */}
            {filteredEntries.length > 0 && <div className="space-y-2" data-id="element-819">
                <h3 className="text-sm font-semibold text-gray-900" data-id="element-820">Entries</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto" data-id="element-821">
                  {filteredEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(entry => <div key={entry.id} className="p-3 bg-gray-50 rounded-lg" data-id="element-822">
                        <div className="flex items-start justify-between mb-2" data-id="element-823">
                          <div className="flex-1 min-w-0" data-id="element-824">
                            <div className="flex items-center gap-2 mb-1" data-id="element-825">
                              <CalendarIcon className="w-3 h-3 text-gray-400" data-id="element-826" />
                              <span className="text-sm font-medium text-gray-900" data-id="element-827">
                                {new Date(entry.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mb-1" data-id="element-828">
                              <FolderIcon className="w-3 h-3 text-gray-400" data-id="element-829" />
                              <span className="text-xs text-gray-600" data-id="element-830">
                                {getProjectName(entry.project_id)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-831">
                              <UserIcon className="w-3 h-3 text-gray-400" data-id="element-832" />
                              <span className="text-xs text-gray-600" data-id="element-833">
                                {entry.resource_name}
                              </span>
                            </div>
                          </div>
                          <div className="text-right ml-3" data-id="element-834">
                            <span className="text-lg font-bold text-gray-900" data-id="element-835">
                              {entry.hours.toFixed(2)}h
                            </span>
                          </div>
                        </div>
                        {entry.notes && <p className="text-xs text-gray-600 mt-2 pl-5" data-id="element-836">
                            {entry.notes}
                          </p>}
                      </div>)}
                </div>
              </div>}
          </div>
        </Card>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-837">
        <Card data-id="element-838">
          <div className="flex items-center justify-between" data-id="element-839">
            <div data-id="element-840">
              <p className="text-sm text-gray-600 mb-1" data-id="element-841">Total Hours</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-842">
                {getTotalHours(filteredEntries).toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-843">
              <ClockIcon className="w-6 h-6 text-blue-600" data-id="element-844" />
            </div>
          </div>
        </Card>

        <Card data-id="element-845">
          <div className="flex items-center justify-between" data-id="element-846">
            <div data-id="element-847">
              <p className="text-sm text-gray-600 mb-1" data-id="element-848">Active Projects</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-849">
                {Object.keys(projectTotals).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center" data-id="element-850">
              <FolderIcon className="w-6 h-6 text-orange-600" data-id="element-851" />
            </div>
          </div>
        </Card>

        <Card data-id="element-852">
          <div className="flex items-center justify-between" data-id="element-853">
            <div data-id="element-854">
              <p className="text-sm text-gray-600 mb-1" data-id="element-855">Resources</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-856">
                {Object.keys(resourceTotals).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-857">
              <UserIcon className="w-6 h-6 text-purple-600" data-id="element-858" />
            </div>
          </div>
        </Card>

        <Card data-id="element-859">
          <div className="flex items-center justify-between" data-id="element-860">
            <div data-id="element-861">
              <p className="text-sm text-gray-600 mb-1" data-id="element-862">Avg Hours/Entry</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-863">
                {filteredEntries.length > 0 ? (getTotalHours(filteredEntries) / filteredEntries.length).toFixed(2) : '0.00'}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-864">
              <CalendarIcon className="w-6 h-6 text-green-600" data-id="element-865" />
            </div>
          </div>
        </Card>
      </div>

      {/* Add Time Entry */}
      <Card data-id="element-866">
        <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-867">Log Time</h2>
        <div className="space-y-4" data-id="element-868">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-869">
            <div data-id="element-870">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-871">
                Project <span className="text-red-600" data-id="element-872">*</span>
              </label>
              <select value={newEntry.project_id} onChange={e => setNewEntry({
              ...newEntry,
              project_id: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-873">
                <option value="" data-id="element-874">Select Project</option>
                {projects.map(project => <option key={project.id} value={project.id} data-id="element-875">
                    {project.title}
                  </option>)}
              </select>
            </div>

            <div data-id="element-876">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-877">
                Resource <span className="text-red-600" data-id="element-878">*</span>
              </label>
              <select value={newEntry.resource_name} onChange={e => setNewEntry({
              ...newEntry,
              resource_name: e.target.value as ResourceName
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-879">
                <option value="" data-id="element-880">Select Resource</option>
                {RESOURCES.map(resource => <option key={resource} value={resource} data-id="element-881">
                    {resource}
                  </option>)}
              </select>
            </div>

            <div data-id="element-882">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-883">
                Hours <span className="text-red-600" data-id="element-884">*</span>
              </label>
              <input type="number" step="0.25" placeholder="0.00" value={newEntry.hours} onChange={e => setNewEntry({
              ...newEntry,
              hours: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-885" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-886">
                Rounded to nearest 0.25
              </p>
            </div>

            <div data-id="element-887">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-888">
                Date
              </label>
              <input type="date" value={newEntry.date} onChange={e => setNewEntry({
              ...newEntry,
              date: e.target.value
            })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-889" />
            </div>
          </div>

          <div data-id="element-890">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-891">
              Notes
            </label>
            <input type="text" placeholder="What did you work on?" value={newEntry.notes} onChange={e => setNewEntry({
            ...newEntry,
            notes: e.target.value
          })} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-892" />
          </div>

          <div className="flex justify-end" data-id="element-893">
            <Button onClick={handleAddEntry} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-895" />} data-id="element-894">
              Log Time
            </Button>
          </div>
        </div>
      </Card>

      {/* Time Entries List */}
      <Card padding="none" data-id="element-896">
        <div className="p-4 border-b border-gray-200" data-id="element-897">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-898">Time Entries</h2>
        </div>
        {filteredEntries.length === 0 ? <div className="p-8 text-center" data-id="element-899">
            <ClockIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-900" />
            <p className="text-gray-500" data-id="element-901">
              {hasActiveFilters ? 'No time entries match your filters.' : 'No time entries yet. Log your first entry above!'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-902">
            <table className="w-full" data-id="element-903">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-904">
                <tr data-id="element-905">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-906">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-907">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-908">
                    Resource
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-909">
                    Hours
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-910">
                    Notes
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-911">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-912">
                {filteredEntries.map(entry => <tr key={entry.id} className="hover:bg-gray-50" data-id="element-913">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap" data-id="element-914">
                      {new Date(entry.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-915">
                      <div className="flex items-center gap-2" data-id="element-916">
                        <FolderIcon className="w-4 h-4 text-gray-400" data-id="element-917" />
                        {getProjectName(entry.project_id)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-918">
                      <div className="flex items-center gap-2" data-id="element-919">
                        <UserIcon className="w-4 h-4 text-gray-400" data-id="element-920" />
                        {entry.resource_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap" data-id="element-921">
                      {entry.hours.toFixed(2)}h
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600" data-id="element-922">
                      {entry.notes || '-'}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-923">
                      <button onClick={() => deleteTimeEntry(entry.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" title="Delete" data-id="element-924">
                        <TrashIcon className="w-4 h-4" data-id="element-925" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Summary by Resource */}
      {Object.keys(resourceTotals).length > 0 && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-926">
          <Card data-id="element-927">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-928">
              Hours by Resource
            </h2>
            <div className="space-y-3" data-id="element-929">
              {Object.entries(resourceTotals).sort((a, b) => b[1] - a[1]).map(([resource, hours]) => <div key={resource} className="flex items-center justify-between" data-id="element-930">
                    <div className="flex items-center gap-2" data-id="element-931">
                      <UserIcon className="w-4 h-4 text-gray-400" data-id="element-932" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-933">
                        {resource}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-934">
                      {hours.toFixed(2)}h
                    </span>
                  </div>)}
            </div>
          </Card>

          <Card data-id="element-935">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-936">
              Hours by Project
            </h2>
            <div className="space-y-3" data-id="element-937">
              {Object.entries(projectTotals).sort((a, b) => b[1] - a[1]).map(([projectId, hours]) => <div key={projectId} className="flex items-center justify-between" data-id="element-938">
                    <div className="flex items-center gap-2" data-id="element-939">
                      <FolderIcon className="w-4 h-4 text-gray-400" data-id="element-940" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-941">
                        {getProjectName(projectId)}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-942">
                      {hours.toFixed(2)}h
                    </span>
                  </div>)}
            </div>
          </Card>
        </div>}
    </div>;
}